import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const HandEyeSquarePlay = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const canvasRef = useRef(null);
  const requestRef = useRef();
  const navigate = useNavigate();

  // BUG 1 FIX: lastTimeRef used for proper delta-time calculation
  const lastTimeRef = useRef(Date.now());

  const [userPath, setUserPath] = useState([]);

  // BUG 3 FIX: state to show finish message popup before navigating
  const [finishMsg, setFinishMsg] = useState(null);
  const [gamepadConnected, setGamepadConnected] = useState(false); // ✅ auto-detect state

  const ballRef = useRef({ x: 0, y: 0, size: 12, color: "blue" });

  const startTimeRef = useRef(null);
  const nextSampleRef = useRef(0);

  const endLineRef = useRef(false);
  const ballMovedRef = useRef(false);
  const finishedRef = useRef(false);

  const axisCenter = useRef({ x: 0, y: 0 });
  const calibrated = useRef(false);

  const DEADZONE = 0.12;
  const TRIGGER_THRESHOLD = 0.05;

  const sideRef = useRef({
    current: null,
    progress: [],
    lapCompleted: false
  });

  const [gamepadStatus, setGamepadStatus] = useState("No joystick detected");

  const getConnectedGamepad = () => {
    const gps = navigator.getGamepads ? navigator.getGamepads() : [];
    for (let i = 0; i < gps.length; i++) {
      if (gps[i]) return gps[i];
    }
    return null;
  };

  // ================= INIT =================
  useEffect(() => {
    // ✅ Check joystick on mount
    const gp = getConnectedGamepad();
    if (gp) {
      setGamepadConnected(true);
      setGamepadStatus("Joystick connected");
      init();
      requestRef.current = requestAnimationFrame(loop);
    } else {
      setGamepadConnected(false);
      setGamepadStatus("No joystick detected");
    }

    // ✅ Listen for gamepad connect event
    const onConnect = (e) => {
      console.log("Gamepad connected:", e.gamepad);
      setGamepadConnected(true);
      setGamepadStatus("Joystick connected");
      init();
      cancelAnimationFrame(requestRef.current);
      requestRef.current = requestAnimationFrame(loop);
    };

    // ✅ Listen for gamepad disconnect event
    const onDisconnect = () => {
      setGamepadConnected(false);
      setGamepadStatus("No joystick detected");
      cancelAnimationFrame(requestRef.current);
    };

    window.addEventListener("gamepadconnected", onConnect);
    window.addEventListener("gamepaddisconnected", onDisconnect);

    // ✅ Fallback polling — some browsers don't fire gamepadconnected
    const pollInterval = setInterval(() => {
      const gp = getConnectedGamepad();
      if (gp) {
        setGamepadConnected(prev => {
          if (!prev) {
            // just became connected
            setGamepadStatus("Joystick connected");
            init();
            cancelAnimationFrame(requestRef.current);
            requestRef.current = requestAnimationFrame(loop);
          }
          return true;
        });
      } else {
        setGamepadConnected(prev => {
          if (prev) {
            setGamepadStatus("No joystick detected");
            cancelAnimationFrame(requestRef.current);
          }
          return false;
        });
      }
    }, 500);

    return () => {
      clearInterval(pollInterval);
      cancelAnimationFrame(requestRef.current);
      window.removeEventListener("gamepadconnected", onConnect);
      window.removeEventListener("gamepaddisconnected", onDisconnect);
    };
  }, []);

  const init = () => {
    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    ballMovedRef.current = false;
    finishedRef.current = false;
    endLineRef.current = false;
    sideRef.current = { current: null, progress: [], lapCompleted: false };
    penaltyTimeRef.current = 0;
    wasOutsideRef.current = false;

    calibrated.current = false;
    axisCenter.current = { x: 0, y: 0 };

    ballRef.current = {
      x: canvas.width / 2,
      y: canvas.height * 0.3,
      size: 12,
      color: "blue"
    };

    setUserPath([]);

    const gp = getConnectedGamepad();
    if (gp) {
      setGamepadStatus("Joystick connected");
    } else {
      setGamepadStatus("No joystick detected");
    }
  };

  // ================= INPUT =================
  const getInput = () => {
    const gp = getConnectedGamepad();
    if (!gp) return null;

    let rawH = gp.axes[2] ?? 0;
    let rawV = gp.axes[1] ?? 0;

    if (!calibrated.current) {
      if (Math.abs(rawH) < 0.2 && Math.abs(rawV) < 0.2) {
        axisCenter.current = { x: rawH, y: rawV };
        calibrated.current = true;
      }
      return { h: 0, v: 0, r2: 0, l2: 0 };
    }

    let h = rawH - axisCenter.current.x;
    let v = rawV - axisCenter.current.y;

    if (Math.abs(h) < DEADZONE) h = 0;
    if (Math.abs(v) < DEADZONE) v = 0;

    h = Math.max(-1, Math.min(1, h));
    v = Math.max(-1, Math.min(1, v));

    const r2 = gp.buttons[7]?.value ?? 0;
    const l2 = gp.buttons[6]?.value ?? 0;

    return { h, v, r2, l2 };
  };

  // ================= LOOP =================
  const loop = () => {
    const gp = getConnectedGamepad();
    if (gp) {
      setGamepadStatus("Joystick connected");
    } else {
      setGamepadStatus("No joystick detected");
    }

    update();
    draw();
    navigator.getGamepads(); // force refresh
    requestRef.current = requestAnimationFrame(loop);
  };

  const outsideStartRef = useRef(null);
  const penaltyTimeRef = useRef(0);
  const wasOutsideRef = useRef(false);

  const update = () => {
    const input = getInput();
    if (!input) return;

    const ball = ballRef.current;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const now = Date.now();

    // ================= BUG 1 FIX: delta time for consistent speed =================
    // Normalize to 60fps so speed doesn't depend on frame rate
    const rawDt = now - lastTimeRef.current;
    const dt = Math.min(rawDt / 16.67, 2); // cap at 2 to avoid huge jumps after tab switch
    lastTimeRef.current = now;

    // ================= DEADZONE =================
    const DEADZONE_UPDATE = 0.2;
    const cleanInput = (val) => {
      if (Math.abs(val) < DEADZONE_UPDATE) return 0;
      return Number(val.toFixed(2));
    };

    const hInput = cleanInput(input.h);
    const vInput = cleanInput(input.v);
    const isRealInput = hInput !== 0 || vInput !== 0;

    // ================= START =================
    if (!ballMovedRef.current && isRealInput) {
      startTimeRef.current = now;
      nextSampleRef.current = now + 250;
      ballMovedRef.current = true;

      ball.prevX = ball.x;
      ball.prevY = ball.y;

      setUserPath([]);
      recordPosition();

      endLineRef.current = false;
      sideRef.current = { current: null, progress: [], lapCompleted: false };
    }

    // ================= MOVEMENT =================
    const accel = 1 + input.r2 * 2;

    if (ball.vx === undefined) ball.vx = 0;
    if (ball.vy === undefined) ball.vy = 0;

    // ✅ L2 press panna ball completely stop aagum
    if (input.l2 > TRIGGER_THRESHOLD) {
      // BRAKE — full stop
      ball.vx = 0;
      ball.vy = 0;
    } else if (isRealInput) {
      // Normal or accelerated movement
      ball.vx = hInput * 6 * accel * dt;
      ball.vy = vInput * 6 * accel * dt;
    } else {
      // No input — stop
      ball.vx = 0;
      ball.vy = 0;
    }

    if (Math.abs(ball.vx) < 0.01) ball.vx = 0;
    if (Math.abs(ball.vy) < 0.01) ball.vy = 0;

    ball.x += ball.vx;
    ball.y += ball.vy;

    // ================= CLAMP BALL INSIDE SCREEN =================
    // Ball screen vittu veliya pogakodathu
    const ballR = ball.size;
    ball.x = Math.max(ballR, Math.min(w - ballR, ball.x));
    ball.y = Math.max(ballR, Math.min(h - ballR, ball.y));

    // ================= PREV SAFE =================
    if (ball.prevX === undefined) ball.prevX = ball.x;
    if (ball.prevY === undefined) ball.prevY = ball.y;

    // ================= SIDE TRACK =================
    updateSide(ball.x, ball.y);

    // ================= SAMPLING =================
    if (ballMovedRef.current && now >= nextSampleRef.current) {
      recordPosition();
      nextSampleRef.current = now + 1000;
    }

    // ================= ENDLINE =================
    // Show end line ONLY after full lap is completed, and only when ball is on left side
    const cx = w / 2;
    if (!endLineRef.current && sideRef.current.lapCompleted && ball.x < cx) {
      endLineRef.current = true;
    }

    // ================= PENALTY =================
    const isOutside =
      ball.x < w * 0.25 ||
      ball.x > w * 0.75 ||
      ball.y < h * 0.25 ||
      ball.y > h * 0.75;

    const dtSeconds = rawDt / 1000;
    if (isOutside) {
      penaltyTimeRef.current += dtSeconds;
    }

    // ================= FINISH: ball crosses end line LEFT to RIGHT =================
    // End line is the vertical segment at cx between outerSquareY1 and innerSquareY1
    // After lap complete, ball must cross LEFT to RIGHT through that segment to finish
    const endLineY1 = h * 0.25;
    const endLineY2 = h * 0.35;
    const inYRange = ball.y >= endLineY1 && ball.y <= endLineY2;

    const crossedLine =
      ball.prevX <= cx &&
      ball.x > cx &&   // LEFT to RIGHT
      inYRange;         // must be in the end line Y segment only

    // ================= FINAL FINISH =================
    if (
      endLineRef.current &&
      sideRef.current.lapCompleted &&
      crossedLine &&
      !finishedRef.current
    ) {
      finishedRef.current = true;
      finishGame();
    }

    // ================= UPDATE PREV =================
    ball.prevX = ball.x;
    ball.prevY = ball.y;

    // ================= COLOR =================
    ball.color = isOutside ? "red" : "blue";
  };

  // ================= RECORD =================
  const recordPosition = () => {
    const ball = ballRef.current;
    setUserPath(prev => {
      const updated = [...prev, [ball.x, ball.y]];
      updateSide(ball.x, ball.y);
      return updated;
    });
  };

  // ================= SIDE =================
  const updateSide = (x, y) => {
    const w = window.innerWidth;
    const h = window.innerHeight;

    let side = "inside";
    if (y <= h * 0.35) side = "top";
    else if (x >= w * 0.65) side = "right";
    else if (y >= h * 0.65) side = "bottom";
    else if (x <= w * 0.35) side = "left";

    if (side === "inside") return;

    const names = ["top", "right", "bottom", "left"];
    const index = names.indexOf(side);
    const s = sideRef.current;

    if (s.current === null) {
      s.current = index;
      s.progress.push(index);
      return;
    }

    if (index !== s.current) {
      const expectedNext = (s.current + 1) % 4;
      if (index === expectedNext) {
        s.current = index;
        s.progress.push(index);

        if (s.progress.length >= 5) {
          const last5 = s.progress.slice(-5);
          const base = last5[0];
          const expected = [0, 1, 2, 3, 4].map(i => (base + i) % 4);
          if (JSON.stringify(last5) === JSON.stringify(expected)) {
            s.lapCompleted = true;
          }
        }
      } else {
        // Wrong direction — reset progress (do NOT set lapCompleted)
        s.current = index;
        s.progress = [index];
        s.lapCompleted = false;
      }
    }
  };

  // ================= DRAW =================
  const draw = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;

    ctx.clearRect(0, 0, w, h);

    const gp = getConnectedGamepad();
    if (!gp) {
      ctx.font = "24px Arial";
      ctx.textAlign = "center";
      ctx.fillStyle = "red";
      ctx.fillText("Connect joystick & press any key / click", w / 2, h / 2);
      return;
    }

    ctx.strokeStyle = "black";
    ctx.strokeRect(w * 0.25, h * 0.25, w * 0.5, h * 0.5);
    ctx.strokeRect(w * 0.35, h * 0.35, w * 0.3, h * 0.3);

    if (endLineRef.current) {
      ctx.beginPath();
      ctx.moveTo(w / 2, h * 0.25);
      ctx.lineTo(w / 2, h * 0.35);
      ctx.strokeStyle = "red";
      ctx.stroke();
    }

    const ball = ballRef.current;
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, ball.size, 0, Math.PI * 2);
    ctx.fillStyle = ball.color;
    ctx.fill();

    ctx.font = "20px Arial";
    ctx.textAlign = "center";
    ctx.fillStyle = "green";
    ctx.fillText("Joystick connected", w / 2, h - 40);
  };

  // ================= FINISH =================
  const finishGame = () => {
    const endTime = Date.now();
    const testTime = (endTime - startTimeRef.current) / 1000;
    const penaltyTime = penaltyTimeRef.current || 0;
    const totalTime = testTime + penaltyTime;

    const w = window.innerWidth;
    const h = window.innerHeight;

    const result = {
      GameName: "Square Path",
      userPath,
      testTime: Number(testTime.toFixed(2)),
      penaltyTime: Number(penaltyTime.toFixed(2)),
      totalTime: Number(totalTime.toFixed(2)),
      StartBallX: ballRef.current.x,
      StartBallY: ballRef.current.y,
      innerSquareX1: w * 0.35,
      innerSquareY1: h * 0.35,
      innerSquareX2: w * 0.65,
      innerSquareY2: h * 0.65,
      outerSquareX1: w * 0.25,
      outerSquareY1: h * 0.25,
      outerSquareX2: w * 0.75,
      outerSquareY2: h * 0.75,
      ballSize: ballRef.current.size
    };

    // ✅ Show popup IMMEDIATELY — no waiting for server
    setFinishMsg({
      time: result.totalTime,
      testTime: result.testTime,
      penaltyTime: result.penaltyTime,
      score: result
    });

    // ✅ Save to server in background (fire-and-forget, does NOT block popup)
    axios.post("/save-handeye", {
      email: localStorage.getItem("user_email"),
      device: "Gamepad",
      data: { SquarePathData: result }
    }).catch(err => console.error("Save error:", err));
  };

  return (
    <div>
      {/* ✅ Show connect message when joystick not connected */}
      {!gamepadConnected && (
        <div style={{
          position: "fixed",
          top: 0, left: 0,
          width: "100%", height: "100%",
          background: "#1a1a2e",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{ fontSize: "64px", marginBottom: "24px" }}>🎮</div>
          <h2 style={{ color: "white", fontSize: "28px", margin: "0 0 12px" }}>
            Connect Your Joystick
          </h2>
          <p style={{ color: "#aaa", fontSize: "16px", textAlign: "center", maxWidth: "360px" }}>
            Please plug in your joystick / gamepad and press any button to start the test.
          </p>
          <div style={{
            marginTop: "32px",
            width: "12px", height: "12px",
            borderRadius: "50%",
            background: "#e74c3c",
            boxShadow: "0 0 12px #e74c3c",
            animation: "pulse 1.2s infinite"
          }} />
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.4; transform: scale(1.4); }
            }
          `}</style>
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: gamepadConnected ? "block" : "none" }} />

      {/* Finish popup — shows time, user clicks OK then navigates */}
      {finishMsg && (
        <div style={{
          position: "fixed",
          top: 0, left: 0,
          width: "100%", height: "100%",
          background: "rgba(0, 0, 0, 0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 999
        }}>
          <div style={{
            background: "white",
            padding: "40px 48px",
            borderRadius: "16px",
            textAlign: "center",
            minWidth: "300px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)"
          }}>
            <h2 style={{ margin: "0 0 8px", fontSize: "24px", color: "#222" }}>
              Test Complete!
            </h2>
            <p style={{ color: "#666", marginBottom: "20px", fontSize: "15px" }}>
              Square Path finished
            </p>
            <div style={{
              background: "#f5f5f5",
              borderRadius: "10px",
              padding: "16px",
              marginBottom: "24px"
            }}>
              <p style={{ margin: "4px 0", fontSize: "14px", color: "#555" }}>
                Test time: <b>{finishMsg.testTime}s</b>
              </p>
              <p style={{ margin: "4px 0", fontSize: "14px", color: "#d32f2f" }}>
                Penalty time: <b>{finishMsg.penaltyTime}s</b>
              </p>
              <p style={{
                margin: "12px 0 0",
                fontSize: "20px",
                fontWeight: "bold",
                color: "#222"
              }}>
                Total: {finishMsg.time}s
              </p>
            </div>
            <button
              onClick={() => {
                setFinishMsg(null);
                navigate("/hand-eye/line", {
                  state: { currentScore: finishMsg.time }
                });
              }}
              style={{
                padding: "12px 40px",
                background: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "10px",
                fontSize: "18px",
                fontWeight: "bold",
                cursor: "pointer",
                letterSpacing: "0.5px"
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HandEyeSquarePlay;
